// src/components/common/DataTable.js
import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Checkbox,
  IconButton,
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Toolbar,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import EmptyState from './EmptyState';
import { TableSkeleton } from './LoadingSkeletons';

// Renders the actions for a single row. If there are more than 2 actions,
// they collapse into a kebab (⋮) menu; otherwise they stay as inline icons.
// Destructive actions (action.color === 'error') are separated by a divider
// and shown in red within the menu.
const RowActions = ({ actions }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  if (!actions || actions.length === 0) return null;

  // 1–2 actions: inline icons (unchanged behavior)
  if (actions.length <= 2) {
    return (
      <Box display="flex" gap={0.5} justifyContent="flex-end">
        {actions.map((action, i) => (
          <Tooltip key={i} title={action.label || ''}>
            <IconButton
              size="small"
              color={action.color || 'default'}
              onClick={(e) => {
                e.stopPropagation();
                action.onClick(e);
              }}
            >
              {action.icon}
            </IconButton>
          </Tooltip>
        ))}
      </Box>
    );
  }

  // 3+ actions: kebab menu
  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleClose = (e) => {
    if (e) e.stopPropagation();
    setAnchorEl(null);
  };

  // Order: non-destructive first, then destructive (so Delete sits at the bottom)
  const normal = actions.filter((a) => a.color !== 'error');
  const destructive = actions.filter((a) => a.color === 'error');

  const renderItem = (action, i) => (
    <MenuItem
      key={i}
      onClick={(e) => {
        e.stopPropagation();
        handleClose();
        action.onClick(e);
      }}
      sx={action.color === 'error' ? { color: 'error.main' } : undefined}
    >
      {action.icon && (
        <ListItemIcon sx={action.color === 'error' ? { color: 'error.main' } : undefined}>
          {action.icon}
        </ListItemIcon>
      )}
      <ListItemText>{action.label}</ListItemText>
    </MenuItem>
  );

  return (
    <Box display="flex" justifyContent="flex-end">
      <Tooltip title="Actions">
        <IconButton size="small" onClick={handleOpen}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClick={(e) => e.stopPropagation()}
      >
        {normal.map(renderItem)}
        {destructive.length > 0 && normal.length > 0 && <Divider />}
        {destructive.map((a, i) => renderItem(a, `d-${i}`))}
      </Menu>
    </Box>
  );
};

const DataTable = ({
  columns = [],
  data = [],
  loading = false,
  onRowClick,
  rowActions,
  selectable = false,
  onSelectionChange,
  searchable = false,
  searchPlaceholder = 'Search...',
  filters = [],
  emptyMessage = 'No data available',
  emptyType = 'default',
  dense = false,
  stickyHeader = true,
  striped = true,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterValues, setFilterValues] = useState({});

  const handleSort = (columnId) => {
    const isAsc = orderBy === columnId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnId);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelected = filteredAndSortedData.map((row, index) => index);
      setSelected(newSelected);
      if (onSelectionChange) {
        onSelectionChange(newSelected.map((index) => filteredAndSortedData[index]));
      }
    } else {
      setSelected([]);
      if (onSelectionChange) onSelectionChange([]);
    }
  };

  const handleSelect = (index) => {
    const selectedIndex = selected.indexOf(index);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = [...selected, index];
    } else {
      newSelected = selected.filter((i) => i !== selectedIndex);
    }
    setSelected(newSelected);
    if (onSelectionChange) {
      onSelectionChange(newSelected.map((i) => filteredAndSortedData[i]));
    }
  };

  const searchedData = searchable && searchTerm
    ? data.filter((row) =>
        columns.some((col) => {
          const value = row[col.id];
          return value && value.toString().toLowerCase().includes(searchTerm.toLowerCase());
        })
      )
    : data;

  const filteredData = filters.length > 0
    ? searchedData.filter((row) =>
        Object.entries(filterValues).every(([key, value]) =>
          !value || value === 'ALL' || row[key] === value
        )
      )
    : searchedData;

  const sortedData = orderBy
    ? [...filteredData].sort((a, b) => {
        const aVal = a[orderBy];
        const bVal = b[orderBy];
        if (aVal < bVal) return order === 'asc' ? -1 : 1;
        if (aVal > bVal) return order === 'asc' ? 1 : -1;
        return 0;
      })
    : filteredData;

  const filteredAndSortedData = sortedData;

  const paginatedData = filteredAndSortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return <TableSkeleton rows={rowsPerPage} columns={columns.length + (selectable ? 1 : 0)} />;
  }

  if (data.length === 0) {
    return <EmptyState type={emptyType} message={emptyMessage} />;
  }

  return (
    <Paper elevation={0} sx={{ width: '100%' }}>
      {(searchable || filters.length > 0) && (
        <Toolbar sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
          {searchable && (
            <TextField
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ flex: 1, mr: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          )}
          {filters.map((filter) => (
            <FormControl key={filter.id} size="small" sx={{ minWidth: 150, mr: 2 }}>
              <InputLabel>{filter.label}</InputLabel>
              <Select
                value={filterValues[filter.id] || 'ALL'}
                label={filter.label}
                onChange={(e) =>
                  setFilterValues({ ...filterValues, [filter.id]: e.target.value })
                }
              >
                <MenuItem value="ALL">All</MenuItem>
                {filter.options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}
        </Toolbar>
      )}

      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader={stickyHeader} size={dense ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selected.length > 0 && selected.length < filteredAndSortedData.length
                    }
                    checked={
                      filteredAndSortedData.length > 0 &&
                      selected.length === filteredAndSortedData.length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || 'left'}
                  sx={{ fontWeight: 'bold' }}
                >
                  {column.sortable !== false ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
              {rowActions && (
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, index) => {
              const actualIndex = page * rowsPerPage + index;
              const isSelected = selected.indexOf(actualIndex) !== -1;

              return (
                <TableRow
                  key={row.id || actualIndex}
                  hover
                  selected={isSelected}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={{
                    cursor: onRowClick ? 'pointer' : 'default',
                    bgcolor: striped && index % 2 === 0 ? 'grey.50' : 'inherit',
                  }}
                >
                  {selectable && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleSelect(actualIndex)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                  )}
                  {columns.map((column) => {
                    const value = row[column.id];
                    return (
                      <TableCell key={column.id} align={column.align || 'left'}>
                        {column.render ? column.render(row) : value || '-'}
                      </TableCell>
                    );
                  })}
                  {rowActions && (
                    <TableCell align="right">
                      <RowActions actions={rowActions(row)} />
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={filteredAndSortedData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default DataTable;